import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import './ServicesShowcase.css';

const SERVICES = [
  {
    image: '/images/tally-prime-3d-bg-removed.png',
    label: 'TallyPrime',
    desc: 'Complete ERP for accounting, GST, inventory & payroll.',
    route: '/tallyprime',
    color: '#6b7a52',
  },
  {
    image: '/images/webdevelopment/3.jpg',
    label: 'Web Development',
    desc: 'Custom websites, portals & enterprise web applications.',
    route: '/WebDevelopment',
    color: '#5a6e3f',
  },
  {
    image: '/images/mobile/3.jpg',
    label: 'Mobile Apps',
    desc: 'iOS & Android apps built for scale and performance.',
    route: '/MobileDeveloper',
    color: '#4a5c35',
  },
  {
    image: '/images/hardware/1.jpg',
    label: 'Hardware Solutions',
    desc: 'Laptops, peripherals, and complete workstation setups.',
    route: '/HardwareServices',
    color: '#7a8c60',
  },
  {
    image: '/images/cctv/pngtree-cctv-camera-in-3d-rendering-image_3674737-removebg-preview.png',
    label: 'CCTV & Security',
    desc: 'Smart surveillance systems for offices and campuses.',
    route: '/SecuritySystem',
    color: '#96a67e',
  },
  {
    image: '/images/biometric-time-attendance-500x500.webp',
    label: 'Biometrics',
    desc: 'Attendance, access control & workforce management.',
    route: '/BIO',
    color: '#6b7a52',
  },
  {
    image: '/images/servers.jpg',
    label: 'Server Solutions',
    desc: 'On-premise & cloud server setup with data integrity.',
    route: '/ServerPage',
    color: '#5a6e3f',
  },
  {
    image: '/images/printer.webp',
    label: 'Printer Services',
    desc: 'Sales, AMC, and software for all printer brands.',
    route: '/PrinterSoftware',
    color: '#4a5c35',
  },
  {
    image: '/images/antivirus.webp',
    label: 'Antivirus',
    desc: 'Enterprise endpoint security and threat protection.',
    route: '/Antivirus',
    color: '#7a8c60',
  },
  {
    image: '/images/autobackup/autobackup.png',
    label: 'Auto Backup',
    desc: 'Automated cloud backup for business-critical data.',
    route: '/autoBackupSoftware',
    color: '#96a67e',
  },
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const navigate = useNavigate();

  return (
    <motion.div
      ref={ref}
      className="service-card"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: (index % 5) * 0.08, ease: [0.25, 0.1, 0.25, 1] }}
      onClick={() => navigate(service.route)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && navigate(service.route)}
      aria-label={`Learn more about ${service.label}`}
    >
      <div className="service-card__image-wrapper">
        <img src={service.image} alt={service.label} className="service-card__image" />
        {service.tag && (
          <span className="service-tag">{service.tag}</span>
        )}
      </div>
      <div className="service-card__content">
        <h3 className="service-card__label">{service.label}</h3>
        <p className="service-card__desc">{service.desc}</p>
        <span className="service-card__link">
          Learn more <FaArrowRight />
        </span>
      </div>
    </motion.div>
  );
};

const ServicesShowcase = () => {
  const titleRef = useRef(null);
  const inView = useInView(titleRef, { once: true, margin: '-60px' });
  const navigate = useNavigate();

  return (
    <section className="services-showcase" id="services">
      <div className="services-bg-pattern" aria-hidden="true" />

      <motion.div
        ref={titleRef}
        className="services-showcase__header"
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
      >
        <span className="section-eyebrow">Our Services</span>
        <h2 className="section-title">
          Everything Your Business <span className="highlight-text">Needs to Thrive</span>
        </h2>
        <p className="section-subtitle">
          From Tally ERP to full IT infrastructure — we are your single-window technology partner.
        </p>
      </motion.div>

      <div className="services-grid">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.label} service={s} index={i} />
        ))}
      </div>

      <motion.div
        className="services-cta-row"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <button className="btn-primary" onClick={() => navigate('/ServicePage')}>
          View All Services <FaArrowRight />
        </button>
        <button className="btn-secondary" onClick={() => navigate('/solutions')}>
          Explore Solutions
        </button>
      </motion.div>
    </section>
  );
};

export default ServicesShowcase;
